import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'src/hooks/use-translation';

import TelegramSvg from 'icons/colored/telegram.svg';

import { useTelegramConnect } from 'src/hooks/use-telegram-connect';

// ----------------------------------------------------------------------

type Props = {
  collapsed?: boolean;
};

export function TelegramWidget({ collapsed = false }: Props) {
  const { status, connect } = useTelegramConnect();
  const { t } = useTranslation('', { keyPrefix: 'telegram-widget' });

  if (collapsed) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <Box
          role="img"
          aria-label={t(status === 'connected' ? 'collapsed-connected' : 'collapsed-disconnected')}
          sx={{ position: 'relative', width: 40, height: 40 }}
        >
          <TelegramSvg width={40} height={40} style={{ display: 'block' }} />
          {status === 'connected' && (
            <Box
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: 'success.main',
                border: '2px solid',
                borderColor: 'background.paper',
              }}
            />
          )}
        </Box>
      </Box>
    );
  }

  if (status === 'loading') {
    return (
      <Box sx={{ mx: 2, mb: 2 }}>
        <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        m: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: 'background.neutral',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <TelegramSvg width={32} height={32} style={{ display: 'block' }} />
          {status === 'connected' && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: 'success.main',
                border: '2px solid',
                borderColor: 'background.neutral',
              }}
            />
          )}
        </Box>

        <Typography variant="subtitle2" noWrap>
          {t('title')}
        </Typography>

        {status === 'connected' && (
          <Chip
            label={t('connected')}
            size="small"
            sx={{
              ml: 'auto',
              bgcolor: 'success.lighter',
              color: 'success.darker',
              typography: 'caption',
              fontWeight: 600,
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>

      {status === 'connected' ? (
        <Typography variant="caption" color="text.secondary">
          {t('connected-description')}
        </Typography>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {t('description')}
          </Typography>

          <ButtonBase
            onClick={connect}
            sx={{
              width: 1,
              py: 1,
              borderRadius: 1.5,
              typography: 'subtitle2',
              transition: 'filter 0.15s',
              '&:hover': { filter: 'brightness(0.94)' },
              ...(status === 'error'
                ? {
                    bgcolor: 'error.lighter',
                    color: 'error.main',
                  }
                : {
                    bgcolor: 'primary.lighter',
                    color: 'primary.main',
                  }),
            }}
          >
            {status === 'error' ? t('retry') : t('connect')}
          </ButtonBase>
        </>
      )}
    </Box>
  );
}
